import { Stack } from "@mui/system";
import { Autocomplete, Button, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import { useEffect, useState } from "react";
import WebsiteActionFilter from "../WebsiteActionFilter/WebsiteActionFilter";
import CreateNewWebsiteModal from "../CreateActionsModal/CreateNewWebsiteModal";
import { MetaEntitiesEndPointLogic } from "../../../../../../logic/Admin/MetaEntities/MetaEntitiesEndPointLogic";
import {
  selectUserData,
  selectUserToken,
} from "../../../../../../features/userSlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { websiteActionEndPointLogic } from "../../../../../../logic/AssetManagement/Websites/websiteActionEndPointLogic";
import { CheckPermissions } from "../../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import store from "../../../../../../store";
import {
  setPage,
  setSelectedWebsite,
} from "../../../../../../features/websiteActionSlice";
import { useSearchParams } from "react-router-dom";

interface TabPanelProps {
  websiteList: any;

  setReloadTable: any;
}

function WebsiteActionFilterCard({
  websiteList,
  setReloadTable,
}: TabPanelProps) {
  const uniqueWebsiteNames = Array.from(
    new Set(websiteList.map((website: any) => website.ui_site_name))
  );

  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const userData = useSelector(selectUserData) as any;
  const userMail = userData?.data?.user_details?.email;
  const dispatch = useDispatch();

  const [openNewWebsite, setopenNewWebsite] = useState(false);
  const [industryList, setIndustryList] = useState([]);
  const [geoList, setGeoList] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [websiteName, setWebsiteName] = useState("");
  const [websiteType, setWebsiteType] = useState("");
  const [siteTitle, setSiteTitle] = useState("");
  const { selectedWebsite } = useSelector((state: any) => state.websiteAction);
  const [searchParams, setSearchParams] = useSearchParams();

  //-- Get User Permissions for Websites(Create)
  const response = useSelector(selectUserData);
  const hasCreateWebsiteViewPermission = CheckPermissions(
    response?.data?.products?.assets,
    "websites",
    "create_new_websites"
  );

  const handleOpenNewWebsite = () => {
    setopenNewWebsite(true);
  };

  const handleCloseNewWebsite = () => {
    setopenNewWebsite(false);
  };

  useEffect(() => {
    const fetchMetaIndustry = async () => {
      try {
        await MetaEntitiesEndPointLogic.getMetaIndustry(
          oauthTokenHeader,
          setIndustryList
        );
      } catch (error) {
        console.error("Error fetching Meta Entities:", error);
      }
    };

    const fetchMetaGeo = async () => {
      try {
        await MetaEntitiesEndPointLogic.getMetaGeo(
          oauthTokenHeader,
          setGeoList,
          dispatch
        );
      } catch (error) {
        console.error("Error fetching Meta Entities:", error);
      }
    };

    const fetchMetaRegions = async () => {
      try {
        await websiteActionEndPointLogic.getMetaRegions(
          oauthTokenHeader,
          setRegionList
        );
      } catch (error) {
        console.error("Error fetching Meta Entities:", error);
      }
    };
    if (oauthTokenHeader) {
      fetchMetaIndustry();
      fetchMetaGeo();
      fetchMetaRegions();
    }
  }, [oauthTokenHeader, dispatch]);

  useEffect(() => {
    // Update selectedTemplate when searchParams change (e.g., on back/forward navigation)
    store.dispatch(setSelectedWebsite(searchParams.get("search")));
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Stack flexDirection={"row"} justifyContent={"start"} gap={2}>
        <Autocomplete
          data-tracker="39"
          sx={{ width: "300px" }}
          options={uniqueWebsiteNames}
          value={selectedWebsite}
          onChange={(_, newValue) => {
            store.dispatch(setSelectedWebsite(newValue));

            setSearchParams((prevParams) => {
              const updatedParams = new URLSearchParams(prevParams); // Correctly handle params
              if (newValue) {
                updatedParams.set("search", newValue); // Set new value
              } else {
                updatedParams.delete("search"); // Remove from URL if cleared
              }
              updatedParams.set("page", "0"); // Ensure page is set to 0
              return updatedParams;
            });

            store.dispatch(setPage(0));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Site Name"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ marginLeft: "12px" }} />
                  </InputAdornment>
                ),
              }}
              color="secondary"
            />
          )}
          size="small"
        />

        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          sx={{ width: "100%" }}
        >
          <WebsiteActionFilter websiteList={websiteList} />
          {hasCreateWebsiteViewPermission ? (
            <Button
              data-tracker="54"
              variant="contained"
              color="secondary"
              sx={{ whiteSpace: "nowrap" }}
              onClick={handleOpenNewWebsite}
            >
              Create new site <AddIcon />
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              disabled
              sx={{ whiteSpace: "nowrap" }}
            >
              Create new site <AddIcon />
            </Button>
          )}
        </Stack>
      </Stack>
      <CreateNewWebsiteModal
        open={openNewWebsite}
        handleClose={handleCloseNewWebsite}
        setWebsiteName={setWebsiteName}
        websiteName={websiteName}
        setWebsiteType={setWebsiteType}
        websiteType={websiteType}
        industryList={industryList}
        geoList={geoList}
        regionList={regionList}
        setSiteTitle={setSiteTitle}
        siteTitle={siteTitle}
        userMail={userMail}
        setReloadTable={setReloadTable}
      />
    </>
  );
}

export default WebsiteActionFilterCard;
